if (!customElements.get('product-splide')) {
  customElements.define(
    'product-splide',
    class ProductSplide extends HTMLElement {
      constructor() {
        super()
        this.mainSplide = null
        this.thumbnailSplide = null
        this.init()
      }

      init() {
        this.initializeSplides()
        this.initializeVideoObserver()
      }

      initializeSplides() {
        const mainElement = this.querySelector(`.main-splide`)
        const thumbnailElement = this.querySelector(`.thumbnail-splide`)

        if (!mainElement || !thumbnailElement) {
          console.error('Required splide elements not found')
          return
        }

        this.mainSplide = new Splide(mainElement, {
          type: 'slide',
          rewind: true,
          pagination: true,
          arrows: false,
          ariaLabel: 'Product gallery main view',
          i18n: {
            listRole: 'group',
          },
        })

        // Initialize thumbnail Splide
        // Note: We don't use isNavigation because it ties drag to slide selection.
        // Instead, we handle click navigation manually below.
        this.thumbnailSplide = new Splide(thumbnailElement, {
          pagination: false,
          arrows: false,
          fixedHeight: 50,
          fixedWidth: 50,
          gap: 8,
          padding: 8,
          snap: true,
          mediaQuery: 'min',
          breakpoints: {
            640: {
              fixedWidth: 70,
              fixedHeight: 70,
            },
            1024: {
              fixedWidth: 100,
              fixedHeight: 100,
            },
            1280: {
              width: 100,
              height: '100%',
              direction: 'ttb',
              padding: 0,
              fixedWidth: 100,
              fixedHeight: 100,
            },
          },
          ariaLabel: 'Product thumbnail navigation',
          i18n: {
            listRole: 'group',
          },
        })

        this.variantIds = this.dataset.variantIds ? this.dataset.variantIds.split(',') : []
        this.originalVariantIds = this.dataset.originalVariantIds ? this.dataset.originalVariantIds.split(',') : []

        // Initial variant filtering, done before mount to avoid flickering
        this.allMainSlides = Array.from(mainElement.querySelectorAll('.splide__slide'))
        this.allThumbnailSlides = Array.from(thumbnailElement.querySelectorAll('.splide__slide'))

        this.allMainSlides.forEach((slide) => {
          if (
            !slide.dataset.variantFilter.includes(this.dataset.currentVariantId) &&
            !slide.dataset.variantFilter.includes(this.variantMap(this.dataset.currentVariantId)) &&
            !slide.dataset.variantFilter.includes(this.dataset.currentVariantTitle) &&
            !slide.dataset.variantFilter.includes('shared')
          ) {
            slide.remove()
          } else {
            slide.querySelectorAll('img').forEach((img) => {
              img.removeAttribute('loading')
            })
          }
        })
        this.allThumbnailSlides.forEach((slide) => {
          if (
            !slide.dataset.variantFilter.includes(this.dataset.currentVariantId) &&
            !slide.dataset.variantFilter.includes(this.variantMap(this.dataset.currentVariantId)) &&
            !slide.dataset.variantFilter.includes(this.dataset.currentVariantTitle) &&
            !slide.dataset.variantFilter.includes('shared')
          ) {
            slide.remove()
          } else {
            slide.querySelectorAll('img').forEach((img) => {
              img.removeAttribute('loading')
            })
          }
        })

        // Mount sliders (no sync - we handle navigation manually)
        this.mainSplide.mount()
        this.thumbnailSplide.mount()

        // Track whether a thumbnail click triggered the navigation
        let thumbnailClickPending = false

        // Handle thumbnail clicks to navigate main slider
        this.thumbnailSplide.on('click', (slide) => {
          thumbnailClickPending = true
          this.mainSplide.go(slide.index)
        })

        // When main slider moves, update thumbnail active state and scroll thumbnail into view
        this.mainSplide.on('move', (newIndex) => {
          this.updateActiveThumbnail(newIndex)
          // Scroll the thumbnail slider to bring the active thumbnail into view
          this.thumbnailSplide.go(newIndex)
        })

        // Prevent thumbnail drag from changing the active slide
        // After any thumbnail slider movement, ensure active state matches main slider
        this.thumbnailSplide.on('moved', () => {
          thumbnailClickPending = false
          // Use requestAnimationFrame to ensure this runs after Splide's internal updates
          requestAnimationFrame(() => {
            this.updateActiveThumbnail(this.mainSplide.index)
          })
        })

        // Also catch the drag end to remove any is-active Splide might have added
        this.thumbnailSplide.on('dragged', () => {
          requestAnimationFrame(() => {
            this.updateActiveThumbnail(this.mainSplide.index)
          })
        })

        // Set initial active thumbnail
        this.updateActiveThumbnail(0)

        // Fix ARIA attributes - remove aria-orientation since role="group" doesn't support it
        const removeAriaOrientation = () => {
          const mainList = mainElement.querySelector('.splide__list')
          const thumbnailList = thumbnailElement.querySelector('.splide__list')
          if (mainList && mainList.getAttribute('aria-orientation')) {
            mainList.setAttribute('role', 'group')
            mainList.removeAttribute('aria-orientation')
          }
          if (thumbnailList && thumbnailList.getAttribute('aria-orientation')) {
            thumbnailList.setAttribute('role', 'group')
            thumbnailList.removeAttribute('aria-orientation')
          }
        }

        // Initial cleanup after mount
        removeAriaOrientation()

        SplideHorizontalScroll(this.mainSplide)
        SplideHorizontalScroll(this.thumbnailSplide, {
          verticalBreakpointPx: 1280,
          disableAutoSettle: true,
        })

        // Listen for Splide events that might re-add aria-orientation
        this.thumbnailSplide.on('mounted', removeAriaOrientation)
        this.thumbnailSplide.on('updated', removeAriaOrientation)
        this.thumbnailSplide.on('resize', removeAriaOrientation)

        this.mainSplide.on('mounted', removeAriaOrientation)
        this.mainSplide.on('updated', removeAriaOrientation)
        this.mainSplide.on('resize', removeAriaOrientation)
      }

      updateActiveThumbnail(activeIndex) {
        const slides = this.thumbnailSplide.Components.Elements.slides
        slides.forEach((slide, index) => {
          // Remove Splide's is-active class (we manage active state ourselves)
          slide.classList.remove('is-active')

          if (index === activeIndex) {
            slide.classList.add('is-current-thumb')
            slide.setAttribute('aria-current', 'true')
          } else {
            slide.classList.remove('is-current-thumb')
            slide.removeAttribute('aria-current')
          }
        })
      }

      variantMap(mainProductVariantId) {
        if (!mainProductVariantId || !this.originalVariantIds.length || !this.variantIds.length) {
          return null
        }

        const mainIndex = this.originalVariantIds.indexOf(mainProductVariantId.toString())

        if (mainIndex === -1 || mainIndex >= this.variantIds.length) {
          return null
        }

        return this.variantIds[mainIndex]
      }

      filterSlides(variant) {
        if (!variant || !variant.id) {
          console.warn('filterSlides called with invalid variant')
          return
        }

        const variantId = variant.id.toString()
        const variantTitle = variant.title ? variant.title.toLowerCase() : ''

        const filteredMainSlides = this.allMainSlides.filter(
          (slide) =>
            slide.dataset.variantFilter.includes(variantId) ||
            slide.dataset.variantFilter.includes(this.variantMap(variantId)) ||
            slide.dataset.variantFilter.includes(variantTitle) ||
            slide.dataset.variantFilter.includes('shared')
        )
        const filteredThumbnailSlides = this.allThumbnailSlides.filter(
          (slide) =>
            slide.dataset.variantFilter.includes(variantId) ||
            slide.dataset.variantFilter.includes(this.variantMap(variantId)) ||
            slide.dataset.variantFilter.includes(variantTitle) ||
            slide.dataset.variantFilter.includes('shared')
        )

        this.mainSplide.remove('*')
        this.thumbnailSplide.remove('*')

        // remove loading="lazy" from all slides
        filteredMainSlides.forEach((slide) => {
          slide.querySelectorAll('img').forEach((img) => {
            img.removeAttribute('loading')
          })
        })
        filteredThumbnailSlides.forEach((slide) => {
          slide.querySelectorAll('img').forEach((img) => {
            img.removeAttribute('loading')
          })
        })

        this.mainSplide.add(filteredMainSlides)
        this.thumbnailSplide.add(filteredThumbnailSlides)

        // Reset to first slide and update active thumbnail
        this.mainSplide.go(0)
        this.thumbnailSplide.go(0)
        this.updateActiveThumbnail(0)
      }

      initializeVideoObserver() {
        if (window.videoObserver && typeof window.videoObserver.observe === 'function') {
          const videos = this.querySelectorAll('video')
          videos.forEach((video) => {
            window.videoObserver.observe(video)
          })
        }
      }

      disconnectedCallback() {
        this.destroy()
      }

      destroy() {
        if (this.mainSplide) {
          this.mainSplide.destroy()
          this.mainSplide = null
        }
        if (this.thumbnailSplide) {
          this.thumbnailSplide.destroy()
          this.thumbnailSplide = null
        }
      }
    }
  )
}
